import { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MdChevronRight,
  MdClose,
  MdEmail,
  MdFavorite,
  MdGroups,
  MdMenu,
  MdMenuBook,
  MdNotificationsNone,
  MdOutlineDarkMode,
  MdOutlineLightMode,
  MdPerson,
  MdPhone,
  MdVolunteerActivism,
} from "react-icons/md";
import toast from "react-hot-toast";
import { useDarkmode } from "hooks/useTheme";
import { useAuth } from "hooks/useAuth";
import Avatar from "components/avatar";
import Loader from "components/loader/Loader";
import { EmptyState } from "components/states";
import heartbeatActionItemService from "services/heartbeatActionItemService";

const ICON_BUTTON =
  "focus-ring relative flex h-10 w-10 items-center justify-center rounded-control text-fg-muted transition-colors hover:bg-surface-subtle hover:text-fg";

const ACTION_TYPE_ICONS = {
  pastoral_care: <MdFavorite className="h-4 w-4 text-red-500" />,
  follow_up_call: <MdPhone className="h-4 w-4 text-blue-500" />,
  volunteer_interest: <MdVolunteerActivism className="h-4 w-4 text-green-500" />,
  resource_delivery: <MdMenuBook className="h-4 w-4 text-purple-500" />,
  group_recommendation: <MdGroups className="h-4 w-4 text-teal-500" />,
  contact_request: <MdEmail className="h-4 w-4 text-orange-500" />,
};

const PRIORITY_DOTS = {
  urgent: "bg-danger",
  high: "bg-warning",
  medium: "bg-info",
};

const formatTimeAgo = (dateString) => {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

/**
 * Disclosure button with a popover panel. Closes on outside click, Escape
 * (focus returns to the button) and on any click inside the panel unless the
 * click handler stops propagation.
 */
function NavPopover({ label, trigger, panelClassName, children }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className={ICON_BUTTON}
      >
        {trigger}
      </button>
      {open && (
        // Clicks bubbling out of the panel (links, row buttons) close it.
        <div
          id={panelId}
          onClick={() => setOpen(false)}
          className={`absolute top-full z-40 mt-2 origin-top-right rounded-card border border-line bg-surface shadow-overlay ${panelClassName}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Admin top bar: breadcrumb (module › page) on the left; action-item bell,
 * theme toggle and account menu on the right. Sticky so the controls stay
 * reachable on long pages.
 *
 * @param {object} props
 * @param {() => void} props.onOpenSidenav Opens the sidebar drawer below xl.
 * @param {Array<{name: string, path: string}>} props.breadcrumb Active route trail from the admin layout.
 */
const Navbar = ({ onOpenSidenav, breadcrumb }) => {
  const { darkmode, setDarkmode } = useDarkmode();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [actionItemStats, setActionItemStats] = useState(null);
  const [recentActionItems, setRecentActionItems] = useState([]);
  const [showMyItems, setShowMyItems] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);

  const fetchActionItems = async () => {
    if (!user) return;
    setLoadingItems(true);
    try {
      const [statsRes, itemsRes] = await Promise.all([
        heartbeatActionItemService.getStats(),
        showMyItems
          ? heartbeatActionItemService.getMyActionItems({ status: "pending" })
          : heartbeatActionItemService.getActionItems({ status: "pending", per: 20 }),
      ]);
      setActionItemStats(statsRes.data || statsRes);
      // getMyActionItems returns { data: items[] }, getActionItems returns { data: items[], pagination }
      const items = itemsRes.data || [];
      // Only items with contact info (not truly anonymous) are actionable from the bell.
      const contactableItems = Array.isArray(items)
        ? items.filter((item) => {
            const hasGuestInfo = item.guest?.firstName || item.guest?.email;
            const hasFollowUpInfo = item.response?.followUpName || item.response?.followUpEmail;
            return hasGuestInfo || hasFollowUpInfo;
          })
        : [];
      setRecentActionItems(contactableItems.slice(0, 5));
    } catch (error) {
      console.error("Error fetching action items:", error);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleDismissItem = async (e, itemId) => {
    e.stopPropagation();
    try {
      await heartbeatActionItemService.updateActionItem(itemId, { status: "dismissed" });
      setRecentActionItems((prev) => prev.filter((item) => item.id !== itemId));
      setActionItemStats((prev) =>
        prev ? { ...prev, pending: Math.max(0, (prev.pending || 0) - 1) } : prev
      );
    } catch (error) {
      console.error("Error dismissing item:", error);
      toast.error("Failed to dismiss");
    }
  };

  const handleDismissAll = async (e) => {
    e.stopPropagation();
    if (recentActionItems.length === 0) return;

    try {
      await Promise.all(
        recentActionItems.map((item) =>
          heartbeatActionItemService.updateActionItem(item.id, { status: "dismissed" })
        )
      );
      const dismissedCount = recentActionItems.length;
      setRecentActionItems([]);
      setActionItemStats((prev) =>
        prev ? { ...prev, pending: Math.max(0, (prev.pending || 0) - dismissedCount) } : prev
      );
      toast.success(`Dismissed ${dismissedCount} items`);
    } catch (error) {
      console.error("Error dismissing items:", error);
      toast.error("Failed to dismiss items");
    }
  };

  useEffect(() => {
    if (user) {
      fetchActionItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, showMyItems]);

  if (!user) return null;

  const pendingCount = actionItemStats?.pending || 0;
  const bellLabel = pendingCount > 0 ? `Action items, ${pendingCount} pending` : "Action items";

  const filterButton = (selected) =>
    `rounded-md px-2 py-1 text-xs font-medium transition-colors focus-ring ${
      selected ? "bg-surface text-fg shadow-sm" : "text-fg-muted hover:text-fg"
    }`;

  return (
    <header className="sticky top-0 z-30 border-b border-line/60 bg-canvas/80 backdrop-blur">
      <div className="flex h-16 items-center gap-2 px-4 md:px-6">
        <button
          type="button"
          aria-label="Open sidebar"
          onClick={onOpenSidenav}
          className={`${ICON_BUTTON} -ml-2 xl:hidden`}
        >
          <MdMenu aria-hidden="true" className="h-5 w-5" />
        </button>

        <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
          <ol className="flex min-w-0 items-center gap-1.5 text-sm text-fg-muted">
            {breadcrumb.map((crumb, index) => {
              const isCurrent = index === breadcrumb.length - 1;
              return (
                <li key={`${crumb.path}-${index}`} className="flex min-w-0 items-center gap-1.5">
                  {isCurrent ? (
                    <span aria-current="page" className="truncate font-medium text-fg">
                      {crumb.name}
                    </span>
                  ) : (
                    <>
                      <Link
                        to={crumb.path}
                        className="focus-ring truncate rounded transition-colors hover:text-fg hover:underline"
                      >
                        {crumb.name}
                      </Link>
                      <MdChevronRight aria-hidden="true" className="h-4 w-4 shrink-0" />
                    </>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="flex shrink-0 items-center gap-1">
          <NavPopover
            label={bellLabel}
            panelClassName="-right-24 w-[calc(100vw-2rem)] max-w-[360px] sm:right-0"
            trigger={
              <>
                <MdNotificationsNone aria-hidden="true" className="h-5 w-5" />
                {pendingCount > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-2xs font-bold text-white"
                  >
                    {pendingCount > 9 ? "9+" : pendingCount}
                  </span>
                )}
              </>
            }
          >
            <div className="flex items-center justify-between gap-2 border-b border-line p-4">
              <div className="flex items-center gap-2">
                <p className="text-base font-bold text-fg">Action Items</p>
                {recentActionItems.length > 0 && (
                  <button
                    type="button"
                    onClick={handleDismissAll}
                    className="focus-ring rounded text-xs text-fg-muted transition-colors hover:text-danger-fg"
                  >
                    Clear All
                  </button>
                )}
              </div>
              <div className="flex gap-1 rounded-control bg-surface-subtle p-1">
                <button
                  type="button"
                  aria-pressed={showMyItems}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMyItems(true);
                  }}
                  className={filterButton(showMyItems)}
                >
                  My Items
                </button>
                <button
                  type="button"
                  aria-pressed={!showMyItems}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMyItems(false);
                  }}
                  className={`relative ${filterButton(!showMyItems)}`}
                >
                  All Items
                  {showMyItems && pendingCount > 0 && (
                    <span
                      aria-hidden="true"
                      className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-danger"
                    />
                  )}
                </button>
              </div>
            </div>

            <div className="max-h-[300px] overflow-y-auto">
              {loadingItems ? (
                <Loader size="sm" label="Loading action items" />
              ) : recentActionItems.length === 0 ? (
                <EmptyState variant="inline" title="No pending action items" />
              ) : (
                <ul>
                  {recentActionItems.map((item) => {
                    const guestName =
                      item.guest?.firstName && item.guest?.lastName
                        ? `${item.guest.firstName} ${item.guest.lastName}`
                        : item.response?.followUpName || "Anonymous Guest";
                    const initials =
                      guestName !== "Anonymous Guest"
                        ? guestName
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2)
                        : "?";

                    return (
                      <li
                        key={item.id}
                        className="group relative border-b border-line last:border-b-0 hover:bg-surface-subtle"
                      >
                        <button
                          type="button"
                          onClick={() => navigate("/admin/heartbeat/action-items")}
                          className="focus-ring flex w-full items-start gap-3 p-3 pr-10 text-left"
                        >
                          <span
                            aria-hidden="true"
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-raised text-sm font-semibold text-fg"
                          >
                            {initials}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-fg">
                              {guestName}
                            </span>
                            <span className="block truncate text-xs capitalize text-fg-muted">
                              {item.actionType?.replace(/_/g, " ") || "Action needed"}
                            </span>
                            <span className="mt-1 flex items-center gap-2 text-2xs text-fg-muted">
                              <span
                                aria-hidden="true"
                                className={`h-2 w-2 rounded-full ${
                                  PRIORITY_DOTS[item.priority] || "bg-fg-muted"
                                }`}
                              />
                              <span className="capitalize">{item.priority}</span>
                              {item.createdAt && <span>{formatTimeAgo(item.createdAt)}</span>}
                            </span>
                          </span>
                          <span aria-hidden="true" className="shrink-0">
                            {ACTION_TYPE_ICONS[item.actionType] || (
                              <MdPerson className="h-4 w-4 text-fg-muted" />
                            )}
                          </span>
                        </button>
                        <button
                          type="button"
                          aria-label={`Dismiss ${guestName}`}
                          title="Dismiss"
                          onClick={(e) => handleDismissItem(e, item.id)}
                          className="focus-ring absolute right-2 top-2 rounded-full p-1 text-fg-muted opacity-0 transition-opacity hover:bg-surface-raised hover:text-danger-fg focus-visible:opacity-100 group-hover:opacity-100"
                        >
                          <MdClose aria-hidden="true" className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <Link
              to="/admin/heartbeat/action-items"
              className="focus-ring flex items-center justify-center gap-1 rounded-b-card border-t border-line p-3 text-sm font-medium text-primary transition-colors hover:bg-surface-subtle dark:text-accent"
            >
              View All Action Items
              <MdChevronRight aria-hidden="true" className="h-4 w-4" />
            </Link>
          </NavPopover>

          <button
            type="button"
            aria-label={darkmode ? "Switch to light mode" : "Switch to dark mode"}
            onClick={() => setDarkmode(!darkmode)}
            className={ICON_BUTTON}
          >
            {darkmode ? (
              <MdOutlineLightMode aria-hidden="true" className="h-5 w-5" />
            ) : (
              <MdOutlineDarkMode aria-hidden="true" className="h-5 w-5" />
            )}
          </button>

          <NavPopover
            label="Account menu"
            panelClassName="right-0 w-56 py-2"
            trigger={<Avatar user={user} size="sm" />}
          >
            <div className="px-4 pb-2 pt-1">
              <p className="truncate text-sm font-semibold text-fg">
                {user?.firstName} {user?.lastName}
              </p>
              {user?.role && (
                <p className="truncate text-xs capitalize text-fg-muted">{user.role}</p>
              )}
            </div>
            <div className="my-1 h-px bg-line" />
            <Link
              to="/admin/settings"
              className="focus-ring block px-4 py-2 text-sm text-fg transition-colors hover:bg-surface-subtle"
            >
              Settings
            </Link>
            <button
              type="button"
              onClick={logout}
              className="focus-ring block w-full px-4 py-2 text-left text-sm text-danger-fg transition-colors hover:bg-surface-subtle"
            >
              Log Out
            </button>
          </NavPopover>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
